/**
 * collection-ranking.js
 * Web Component that fetches best-selling products for a collection
 * and renders ranked product cards in a horizontal carousel.
 *
 * Attributes:
 * - data-collection-handle: The collection handle to fetch products from
 * - data-limit: Maximum number of products to display (default: 10)
 */

if (!customElements.get('collection-ranking')) {
  customElements.define(
    'collection-ranking',
    class CollectionRanking extends HTMLElement {
      connectedCallback() {
        this.handle = this.dataset.collectionHandle;
        this.limit = parseInt(this.dataset.limit, 10) || 10;

        if (!this.handle) return;

        this.fetchProducts();
      }

      async fetchProducts() {
        try {
          const res = await fetch(
            `/collections/${this.handle}?view=ranking&sort_by=best-selling`
          );
          if (!res.ok) throw new Error(`HTTP ${res.status}`);

          const data = await res.json();
          const products = (data.products || []).slice(0, this.limit);

          if (products.length < 3) {
            const section = this.closest('[data-ranking-section]');
            if (section) section.style.display = 'none';
            return;
          }

          this.renderProducts(products);
        } catch (err) {
          console.error('[collection-ranking] Failed to fetch products:', err);
          const section = this.closest('[data-ranking-section]');
          if (section) section.style.display = 'none';
        }
      }

      renderProducts(products) {
        const container = document.createElement('div');
        container.className =
          'flex gap-grid-md overflow-x-auto pb-inset-md scrollbar-hide snap-x snap-mandatory';

        products.forEach((product, index) => {
          const image = product.images && product.images[0]
            ? product.images[0].src
            : '';
          const price = this.formatPrice(product.variants[0].price);
          const compareAtPrice = product.variants[0].compare_at_price;
          const url = `/products/${product.handle}`;
          const vendor = product.vendor || '';

          const card = document.createElement('div');
          card.className =
            'flex-shrink-0 w-[42vw] md:w-card-carousel-sm lg:w-card-carousel-md snap-start relative';

          card.innerHTML = `
            <div class="absolute top-[var(--space-primitive-8)] left-[var(--space-primitive-8)] z-10 w-[var(--space-primitive-28)] h-[var(--space-primitive-28)] md:w-[var(--space-primitive-32)] md:h-[var(--space-primitive-32)] bg-surface-primary text-text-foreground-high rounded-full flex items-center justify-center text-note-sm md:text-note-md font-bold shadow-sm">
              ${index + 1}
            </div>
            <a href="${url}" class="block group/card">
              <div class="relative bg-surface-neutral-tertiary rounded-[8px] overflow-hidden mb-[var(--space-stack-xs)] aspect-square">
                ${image ? `<img src="${image}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="440" height="440" class="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500 ease-out">` : '<div class="w-full h-full flex items-center justify-center text-text-neutral-disabled">No Image</div>'}
              </div>
              ${vendor ? `<p class="text-note-sm text-text-neutral-low mb-[var(--space-primitive-4)] truncate">${this.escapeHtml(vendor)}</p>` : ''}
              <h3 class="text-label-sm text-text-neutral-high font-medium leading-tight mb-[var(--space-primitive-8)] line-clamp-2">${this.escapeHtml(product.title)}</h3>
              <div class="flex items-baseline gap-[var(--space-primitive-8)]">
                ${compareAtPrice && parseFloat(compareAtPrice) > parseFloat(product.variants[0].price) ? `
                  <span class="text-label-lg text-text-destructive-default">${price}</span>
                  <span class="text-body-md text-text-neutral-disabled line-through">${this.formatPrice(compareAtPrice)}</span>
                ` : `
                  <span class="text-label-lg text-text-neutral-high">${price}</span>
                `}
              </div>
            </a>
          `;

          container.appendChild(card);
        });

        this.innerHTML = '';
        this.appendChild(container);
      }

      formatPrice(price) {
        const num = Math.round(parseFloat(price));
        return `¥${num.toLocaleString('ja-JP')}`;
      }

      escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
      }
    }
  );
}
